import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { Colors } from '../../../assets';
import { Spacing } from '../../../assets/GlobalStyle/Variables';
import { Tabs, Tab } from '../../../common/components/Tabs/Tabs';
import { AsyncControlSelect, AsyncOption } from '../../../common/components/Form';
import { useAutopsyProtocol } from './useAutopsyProtocol';

type DiagnosisProps = {
  disabled?: boolean;
};

type DiagnosisDescriptionProps = {
  fieldName: string;
  loadDiagnoseOption: (id: number) => Promise<AsyncOption | null>;
};

export const Diagnosis: FC<DiagnosisProps> = ({ disabled = false }): JSX.Element => {
  const { fields, loadDiagnosesOptions, loadDiagnoseOption } = useAutopsyProtocol();

  return (
    <>
      <Tabs tabListJustify="space-between">
        <Tab title="Klinika DG">
          <DiagnosisRow>
            <Col sm={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.clinical_dg1}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.clinical_dg1.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.clinical_dg2}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.clinical_dg2.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.clinical_dg3}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.clinical_dg3.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>
        </Tab>

        <Tab title="Pitevní DG">
          <DiagnosisRow>
            <Col sm={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.autopsy_dg1}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.autopsy_dg1.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.autopsy_dg2}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.autopsy_dg2.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.autopsy_dg3}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.autopsy_dg3.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>
        </Tab>

        <Tab title="Mechanismus smrti">
          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.mechanism_dg1}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.mechanism_dg1.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.mechanism_dg2}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.mechanism_dg2.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.mechanism_dg3}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.mechanism_dg3.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.mechanism_dg4}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.mechanism_dg4.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>

          <DiagnosisRow>
            <Col md={12} className="narrow-label">
              <AsyncControlSelect
                {...fields.mechanism_dg5}
                loadOptions={loadDiagnosesOptions}
                loadInitialOption={loadDiagnoseOption}
                disabled={disabled}
              />
            </Col>
            <DiagnosisDescription
              fieldName={fields.mechanism_dg5.name}
              loadDiagnoseOption={loadDiagnoseOption}
            />
          </DiagnosisRow>
        </Tab>
      </Tabs>
    </>
  );
};

const DiagnosisDescription = ({ fieldName, loadDiagnoseOption }: DiagnosisDescriptionProps) => {
  const { watch } = useFormContext();
  const selectedValue = watch(fieldName);
  const [description, setDescription] = useState('');

  useEffect(() => {
    const diagnosisId = Number(selectedValue);
    if (!selectedValue || !Number.isFinite(diagnosisId) || diagnosisId <= 0) {
      setDescription('');
      return;
    }

    let active = true;

    loadDiagnoseOption(diagnosisId).then((option) => {
      if (active) {
        setDescription(option?.description || '');
      }
    });

    return () => {
      active = false;
    };
  }, [selectedValue, loadDiagnoseOption, fieldName]);

  return <StyledDiagnosisDescription>{description}</StyledDiagnosisDescription>;
};

const DiagnosisRow = styled(Row)`
  margin-bottom: ${Spacing(4)};
`;

const StyledDiagnosisDescription = styled.p`
  color: ${Colors.grey};
  font-size: 14px;
  white-space: nowrap;
  width: 100%;
  min-height: 14px;
  margin: -${Spacing(4)} 0 ${Spacing(4)} 4rem;
  padding: 0 ${Spacing(2)};
`;
